import React, { useEffect, useState } from "react";
import { useParams, Link, useHistory } from "react-router-dom";
import { useMyContext } from "../MyContext";
import { ChevronLeftIcon } from "@heroicons/react/solid";
import {
  FaBuilding,
  FaFlask,
  FaIndustry,
  FaRoad,
  FaShieldAlt,
  FaTint,
  FaTools,
  FaTractor,
} from "react-icons/fa";

const getApplicationIcon = (application, index) => {
  const applicationName = application.toLowerCase();

  if (/agricultur|irrigation|farm/.test(applicationName)) return FaTractor;
  if (/industrial|factory/.test(applicationName)) return FaIndustry;
  if (/building|municipal|residential|commercial/.test(applicationName)) return FaBuilding;
  if (/chemical|gas/.test(applicationName)) return FaFlask;
  if (/road|transport/.test(applicationName)) return FaRoad;
  if (/water|rain|drain|sewer|pump|supply|bore/.test(applicationName)) return FaTint;

  const fallbackIcons = [FaTools, FaBuilding, FaIndustry, FaTint];
  return fallbackIcons[index % fallbackIcons.length];
};

const getCompositionIcon = (composition, index) => {
  const compositionName = composition.toLowerCase();

  if (/resin|polyvinyl|polyethylene|compound|plastic/.test(compositionName)) return FaFlask;
  if (/uv|thermal|stabilizer|impact|pressure-resistant/.test(compositionName)) return FaShieldAlt;
  if (/processing|additive|modifier|aid/.test(compositionName)) return FaTools;

  const fallbackIcons = [FaFlask, FaShieldAlt, FaTools];
  return fallbackIcons[index % fallbackIcons.length];
};

function ProductDetails() {
  const { id } = useParams();
  const { productObj } = useMyContext();
  const [product, setProduct] = useState({});
  const [mainImage, setMainImage] = useState("");
  const [loading, setLoading] = useState(true);
  const history = useHistory();

  useEffect(() => {
    const locateProduct = () => {
      const foundProduct = productObj?.find((item) => item.id === id);

      if (foundProduct) {
        setProduct(foundProduct);
        setMainImage(foundProduct.image || "");
      } else {
        setProduct(null);
      }
      setLoading(false);
    };

    locateProduct();
  }, [id, productObj]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500"></div>
          <p className="mt-4 text-slate-500 font-medium text-sm">Loading product details...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] bg-white px-6 text-center">
        <h2 className="text-2xl font-bold text-slate-800 mb-2">Product Not Found</h2>
        <p className="text-slate-500 text-sm mb-6 max-w-sm">
          The product you are looking for does not exist or has been relocated.
        </p>
        <button
          onClick={() => history.push("/products")}
          className="bg-orange-600 hover:bg-orange-700 text-white font-bold py-2.5 px-6 rounded-xl text-sm transition-all shadow-md cursor-pointer"
        >
          Back to Products
        </button>
      </div>
    );
  }

  let productImageSrc;
  if (mainImage) {
    try {
      productImageSrc = require(`../image/products/${mainImage}`);
    } catch (e) {
      productImageSrc = null;
    }
  }

  return (
    <div className="w-full bg-white font-sans overflow-hidden">

      {/* Hero Header Section */}
      <div className="relative w-full bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 pt-20 pb-28 md:pt-24 md:pb-36 lg:pt-28 lg:pb-40 px-6 overflow-hidden">

        {/* Ambient Hero Glow Effects */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-white/10 rounded-full blur-3xl pointer-events-none"></div>

        <div className="max-w-4xl mx-auto text-center text-white space-y-4 relative z-10">
          <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-white/10 backdrop-blur-md border border-white/20 mb-2 shadow-sm">
            <span className="w-2 h-2 rounded-full bg-white animate-pulse"></span>
            <span className="text-xs font-bold uppercase tracking-wider text-orange-50">Technical Datasheet</span>
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl font-black tracking-tight drop-shadow-md">
            {product.title}
          </h1>

          <p className="text-sm sm:text-base md:text-lg font-normal leading-relaxed text-orange-50 opacity-95 max-w-2xl mx-auto line-clamp-3">
            {product.description}
          </p>
        </div>

        {/* Clean Solid White Curved SVG Divider */}
        <div className="absolute bottom-0 left-0 right-0 w-full overflow-hidden leading-[0] z-10">
          <svg
            className="relative block w-full h-[60px] md:h-[100px]"
            viewBox="0 0 1920 120"
            preserveAspectRatio="none"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0,120 C640,30 1280,30 1920,120 L1920,120 L0,120 Z"
              fill="#ffffff"
            />
          </svg>
        </div>
      </div>

      {/* Breadcrumbs Section */}
      <div className="bg-white py-3.5 px-6 md:px-12 border-b border-slate-100 relative z-20">
        <div className="max-w-7xl mx-auto flex items-center space-x-2 text-xs sm:text-sm font-medium text-slate-500">
          <Link
            to="/"
            className="text-orange-600 hover:text-orange-700 font-semibold transition-colors"
          >
            Home
          </Link>
          <span className="text-slate-300 font-light">&gt;</span>
          <Link
            to="/products"
            className="text-orange-600 hover:text-orange-700 font-semibold transition-colors"
          >
            Products
          </Link>
          <span className="text-slate-300 font-light">&gt;</span>
          <span className="text-slate-800">{product.title}</span>
        </div>
      </div>

      {/* Main Details Section */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">

          {/* Product Image Frame */}
          <div className="lg:col-span-5">
            <div className="relative bg-slate-50 border border-slate-200/80 rounded-3xl p-8 shadow-xl shadow-slate-200/50 overflow-hidden flex items-center justify-center">
              <span className="absolute top-4 left-4 z-10 bg-slate-900/80 backdrop-blur-md text-white text-[10px] font-bold px-3 py-1 rounded-full tracking-wider uppercase border border-white/10">
                BIS Certified
              </span>

              <div className="aspect-square w-full flex items-center justify-center">
                {productImageSrc ? (
                  <img
                    src={productImageSrc}
                    alt={product.title}
                    className="max-w-full max-h-full object-contain rounded-xl hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <div className="text-center text-slate-400">
                    <p className="text-base font-bold">{product.title}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Product Details Overview Panel */}
          <div className="lg:col-span-7 flex flex-col justify-between space-y-8">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold tracking-wider uppercase bg-orange-100 text-orange-700">
                Product Specification
              </div>

              <h1 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
                {product.title}
              </h1>

              <p className="text-slate-600 leading-relaxed text-sm sm:text-base">
                {product.description}
              </p>

              {/* Quick Key Features Highlight */}
              {product.advantages && product.advantages.length > 0 && (
                <div className="bg-orange-50/50 border border-orange-100 rounded-2xl p-6 space-y-3">
                  <h3 className="text-xs font-bold text-orange-700 uppercase tracking-wider">
                    Key Advantages
                  </h3>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {product.advantages.slice(0, 4).map((advantage, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-xs sm:text-sm text-slate-700">
                        <span className="w-1.5 h-1.5 rounded-full bg-orange-500 shrink-0"></span>
                        <span className="line-clamp-1">{advantage}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Action Triggers Bar */}
            <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-slate-100">
              <button
                onClick={() => history.push("/products")}
                className="flex-1 flex items-center justify-center space-x-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold py-3 px-5 rounded-xl transition-all text-xs sm:text-sm cursor-pointer"
              >
                <ChevronLeftIcon className="w-4 h-4" />
                <span>Back</span>
              </button>

              <a
                href={encodeURI(`${process.env.PUBLIC_URL || ""}/${product.file_name}`)}
                download={product.file_name?.split("/").pop() || "PoloPipes-Catalogue.pdf"}
                className="flex-1 flex items-center justify-center gap-2 bg-orange-100 hover:bg-orange-200 text-orange-700 font-bold py-3 px-5 rounded-xl transition-all text-xs sm:text-sm"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14" />
                </svg>
                <span>Download Catalogue</span>
              </a>

              <Link to="/contact" className="flex-1">
                <button className="w-full bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 hover:opacity-95 text-white font-bold py-3 px-6 rounded-xl transition-all shadow-md shadow-orange-500/20 text-xs sm:text-sm cursor-pointer">
                  Inquire Now
                </button>
              </Link>
            </div>
          </div>
        </div>

        {/* Detailed Technical Specifications Cards */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">

          {product.composition && product.composition.length > 0 && (
            <div className="bg-white rounded-3xl border border-slate-200/80 shadow-lg shadow-slate-200/50 p-6 sm:p-8 space-y-4">
              <h3 className="text-base font-bold text-slate-900 border-b border-slate-100 pb-3 flex items-center justify-between">
                <span>Composition</span>
                <span className="w-2 h-2 rounded-full bg-orange-500"></span>
              </h3>
              <ul className="space-y-2.5">
                {product.composition.map((item, idx) => (
                  <li key={idx} className="flex items-start text-xs sm:text-sm text-slate-600 leading-normal">
                    {(() => {
                      const CompositionIcon = getCompositionIcon(item, idx);
                      return (
                        <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-orange-50 text-orange-600 mr-2.5 shrink-0">
                          <CompositionIcon className="h-3.5 w-3.5" aria-hidden="true" />
                        </span>
                      );
                    })()}
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {product.applications && product.applications.length > 0 && (
            <div className="bg-white rounded-3xl border border-slate-200/80 shadow-lg shadow-slate-200/50 p-6 sm:p-8 space-y-4">
              <h3 className="text-base font-bold text-slate-900 border-b border-slate-100 pb-3 flex items-center justify-between">
                <span>Applications</span>
                <span className="w-2 h-2 rounded-full bg-sky-500"></span>
              </h3>
              <ul className="space-y-2.5">
                {product.applications.map((item, idx) => (
                  <li key={idx} className="flex items-start text-xs sm:text-sm text-slate-600 leading-normal">
                    {(() => {
                      const ApplicationIcon = getApplicationIcon(item, idx);
                      return (
                        <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-sky-50 text-sky-600 mr-2.5 shrink-0">
                          <ApplicationIcon className="h-3.5 w-3.5" aria-hidden="true" />
                        </span>
                      );
                    })()}
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {product.sizes && product.sizes.length > 0 && (
            <div className="bg-white rounded-3xl border border-slate-200/80 shadow-lg shadow-slate-200/50 p-6 sm:p-8 space-y-4">
              <h3 className="text-base font-bold text-slate-900 border-b border-slate-100 pb-3 flex items-center justify-between">
                <span>Available Sizes</span>
                <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
              </h3>
              <ul className="space-y-2.5">
                {product.sizes.map((item, idx) => (
                  <li key={idx} className="flex items-start text-xs sm:text-sm text-slate-600 leading-normal">
                    <span className="inline-block w-1.5 h-1.5 bg-emerald-500 rounded-full mt-1.5 mr-2.5 shrink-0"></span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* All Advantages Grid Section */}
        {product.advantages && product.advantages.length > 4 && (
          <div className="mt-12 bg-slate-50/80 rounded-3xl p-8 sm:p-10 border border-slate-200/80">
            <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-6">
              Complete Feature Advantage
            </h3>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {product.advantages.map((advantage, idx) => (
                <li key={idx} className="flex items-start text-slate-700 text-xs sm:text-sm leading-relaxed">
                  <span className="text-orange-500 mr-2.5 font-bold">✓</span>
                  <span>{advantage}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* Bottom Global Banner */}
      <section className="bg-slate-900 py-16 px-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl pointer-events-none"></div>

        <div className="max-w-4xl mx-auto text-center relative z-10 space-y-6">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold tracking-wider uppercase bg-orange-500/10 text-orange-400 border border-orange-500/20">
            Certified Quality
          </span>

          <h2 className="text-3xl md:text-4xl font-black text-white tracking-tight">
            Explore Our Complete Piping Catalog
          </h2>

          <p className="text-slate-300 max-w-xl mx-auto text-sm md:text-base leading-relaxed">
            Discover our complete product range engineered to standard BIS benchmarks.
          </p>

          <div>
            <Link to="/products">
              <button className="bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 hover:opacity-95 text-white font-bold py-3.5 px-8 rounded-xl transition-all shadow-lg shadow-orange-500/20 text-sm cursor-pointer">
                View All Products
              </button>
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
}

export default ProductDetails;
